import { useMemo } from 'react';

import { useAppSelector } from 'store';
import { useTenantQuery } from 'store/api/auth';
import { Role, ProjectAdminConfig } from 'types';
import { selectAuthUser } from 'store/slices/auth';
import { useAdminConfigQuery } from 'store/api/project';

import { useSelectedProjectId } from './use-selected-project-id';

const tenantContextRoles = [
  Role.TENANT_ADMIN,
  Role.LIBRARY_ADMIN,
  Role.CONTENT_MANAGER,
  Role.REPORT_MANAGER,
];

export function useAdminConfig() {
  const user = useAppSelector(selectAuthUser);
  const { selectedProjectId } = useSelectedProjectId();
  const hasTenantContext = !!user && tenantContextRoles.includes(user.role);
  const tenant = useTenantQuery(undefined, { skip: !hasTenantContext });
  const assignedProjects = user?.projectIds ?? [];
  const isProjectScoped =
    user?.role === Role.PROJECT_ADMIN || user?.role === Role.LHD_MIVAX_ADMIN;
  const scopedProjectId = isProjectScoped
    ? selectedProjectId && assignedProjects.includes(selectedProjectId)
      ? selectedProjectId
      : assignedProjects.length === 1
        ? assignedProjects[0]
        : undefined
    : selectedProjectId;
  const currentTenant = tenant.currentData;
  const tenantMatchesUser =
    !user?.tenantId || currentTenant?.id === user.tenantId;
  const projectId = hasTenantContext
    ? tenantMatchesUser
      ? currentTenant?.projectId
      : undefined
    : scopedProjectId;
  const query = useAdminConfigQuery(
    { id: projectId ?? '' },
    { skip: hasTenantContext || !projectId || !user }
  );
  // currentData belongs to the current argument; never reuse another project's metadata.
  const config = useMemo<ProjectAdminConfig | undefined>(() => {
    if (!projectId) return undefined;
    return hasTenantContext
      ? {
          adminTerminology: currentTenant?.adminTerminology ?? {},
          userTableColumnsByRole: currentTenant?.userTableColumnsByRole ?? {},
        }
      : query.currentData;
  }, [projectId, hasTenantContext, currentTenant, query.currentData]);
  return {
    config,
    projectId,
    tenant: currentTenant,
    configQuery: hasTenantContext ? tenant : query,
  };
}
